import type React from "react"

import { useState, useEffect } from "react"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

import { useOrganization } from "@/lib/organization/organization-context"
import { teamsApi } from "@/lib/api/teams"
import { useTeam } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"
import { ApiError } from "@/lib/api/client"
import { USStateSelect } from "@/components/ui/us-state-select"
import { formatPhone, isValidPhone } from "@/lib/utils/phone"
import { formatZip, isValidZip } from "@/lib/utils/zip"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"

// Mirrors the BE's PATCH /v1/teams/{teamId} (UpdateTeamDto: name) and
// PUT /v1/teams/{teamId}/settings (UpdateTeamSettingsDto: address fields).
// Labor rate / materials / extraFees / vehicleScans live under metadata
// and are owned by the Fees tab.
//
// Takes a teamId + teamKind so each Settings entity-section mounts its
// own scoped instance — a Director navigating between dealerships gets a
// fresh fetch per team. Falls back to the active team for legacy callers.
interface StoreSettingsProps {
  teamId?: number | string | null
  teamKind?: "dealership" | "bodyshop" | "group"
}

export function StoreSettings({
  teamId: teamIdProp,
  teamKind: teamKindProp,
}: StoreSettingsProps = {}) {
  const ctx = useOrganization()
  const teamId = teamIdProp ?? ctx.activeTeamId
  const teamKind = teamKindProp ?? ctx.activeTeamKind
  const queryClient = useQueryClient()

  const { data: team, isLoading } = useTeam(teamId)

  const [saving, setSaving] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [zipTouched, setZipTouched] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
  })

  useEffect(() => {
    if (!team) return
    setFormData({
      name: team.name ?? "",
      street: team.street ?? "",
      city: team.city ?? "",
      state: team.state ?? "",
      zipCode: team.zipCode ? formatZip(team.zipCode) : "",
      phoneNumber: team.phoneNumber ? formatPhone(team.phoneNumber) : "",
    })
  }, [team])

  const phoneIsValid = !formData.phoneNumber || isValidPhone(formData.phoneNumber)
  const zipIsValid = !formData.zipCode || isValidZip(formData.zipCode)
  const showPhoneError = phoneTouched && !!formData.phoneNumber && !phoneIsValid
  const showZipError = zipTouched && !!formData.zipCode && !zipIsValid
  const canSave = phoneIsValid && zipIsValid

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamId || !team) return
    setSaving(true)
    try {
      const numericTeamId = Number(teamId)

      const tasks: Promise<unknown>[] = []
      if (formData.name && formData.name !== team.name) {
        tasks.push(teamsApi.update(numericTeamId, { name: formData.name }))
      }

      tasks.push(
        teamsApi.updateSettings(numericTeamId, {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined,
          country: "United States",
          phoneNumber: formData.phoneNumber || undefined,
        }),
      )

      await Promise.all(tasks)

      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })

      toast.success(
        teamKind === "bodyshop" ? "Body shop details updated" : "Store details updated",
      )
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Failed to update store details"
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (!teamId) {
    return <p className="text-[13px] text-zinc-500">No location selected.</p>
  }

  return (
    <div>
      {isLoading ? (
        <p className="text-sm text-zinc-400">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <Field label="Name">
            <input
              id="store-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Main Street Auto Body"
              maxLength={255}
              className={onboardingInputClass}
            />
          </Field>

          <Field label="Street">
            <input
              id="store-street"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              placeholder="123 Main Street"
              maxLength={255}
              className={onboardingInputClass}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-3">
            <Field label="City">
              <input
                id="store-city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Springfield"
                maxLength={255}
                className={onboardingInputClass}
              />
            </Field>
            <Field label="State">
              <USStateSelect
                id="store-state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className={onboardingInputClass}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="ZIP code"
              error={showZipError ? "Please enter a valid ZIP code" : undefined}
            >
              <input
                id="store-zip"
                inputMode="numeric"
                value={formData.zipCode}
                onChange={(e) =>
                  setFormData({ ...formData, zipCode: formatZip(e.target.value) })
                }
                onBlur={() => setZipTouched(true)}
                placeholder="62701"
                maxLength={10}
                aria-invalid={showZipError || undefined}
                className={cx(onboardingInputClass, showZipError && onboardingInputErrorClass)}
              />
            </Field>
            <Field label="Country">
              <div
                id="store-country"
                aria-disabled
                className={cx(
                  onboardingInputClass,
                  "flex items-center text-zinc-500 cursor-not-allowed bg-zinc-50",
                )}
              >
                United States
              </div>
            </Field>
          </div>

          <Field
            label="Phone"
            error={showPhoneError ? "Please enter a valid phone number" : undefined}
          >
            <input
              id="store-phone"
              type="tel"
              inputMode="tel"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: formatPhone(e.target.value) })
              }
              onBlur={() => setPhoneTouched(true)}
              placeholder="(555) 123-4567"
              maxLength={20}
              aria-invalid={showPhoneError || undefined}
              className={cx(onboardingInputClass, showPhoneError && onboardingInputErrorClass)}
            />
          </Field>

          <div className="pt-2">
            <PrimaryButton loading={saving} disabled={!canSave}>
              {saving ? "Saving…" : "Save changes"}
            </PrimaryButton>
          </div>
        </form>
      )}
    </div>
  )
}
